import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CartActions, SavedCartApiService } from '@cart/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Cart, normalizeHttpError } from '@spartacus/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable()
export class SavedCartEffects {
  loadSavedCart$: Observable<
    CartActions.LoadCartSuccess | CartActions.LoadSavedCartFail | CartActions.LoadSavedCartSuccess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.LOAD_SAVED_CART),
      map((action: CartActions.LoadSavedCart) => action.payload),
      switchMap(({ userId, cartId }) =>
        this.occSavedCartService.load(cartId).pipe(
          switchMap((savedCart: Cart) => {
            return [
              new CartActions.LoadCartSuccess({
                userId,
                cartId,
                cart: savedCart,
              }),
              new CartActions.LoadSavedCartSuccess({ userId, cartId }),
            ];
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              new CartActions.LoadSavedCartFail({
                userId,
                cartId,
                error: normalizeHttpError(error),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadSavedCarts$: Observable<
    CartActions.LoadCartsSuccess | CartActions.LoadSavedCartsFail | CartActions.LoadSavedCartsSuccess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.LOAD_SAVED_CARTS),
      map((action: CartActions.LoadSavedCarts) => action.payload),
      switchMap(({ userId }) =>
        this.occSavedCartService.loadList().pipe(
          switchMap((savedCarts: Cart[]) => {
            return [new CartActions.LoadCartsSuccess(savedCarts), new CartActions.LoadSavedCartsSuccess({ userId })];
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              new CartActions.LoadSavedCartsFail({
                userId,
                error: normalizeHttpError(error),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  cloneSavedCart$: Observable<
    | CartActions.CloneSavedCartFail
    | CartActions.CloneSavedCartSuccess
    | CartActions.CloneSavedCart
    | CartActions.RestoreSavedCart
    | CartActions.LoadSavedCarts
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.CLONE_SAVED_CART),
      map((action: CartActions.CloneSavedCart) => action.payload),
      switchMap(({ userId, cartId, saveCartName }) => {
        return this.occSavedCartService.cloneSavedCart(userId, cartId, saveCartName).pipe(
          switchMap(() => {
            return [
              new CartActions.CloneSavedCartSuccess({
                userId,
                cartId,
                saveCartName,
              }),
              new CartActions.RestoreSavedCart({
                userId,
                cartId,
              }),
              new CartActions.LoadSavedCarts({ userId }),
            ];
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              new CartActions.CloneSavedCartFail({
                userId,
                cartId,
                saveCartName,
                error: normalizeHttpError(error),
              }),
            ),
          ),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private occSavedCartService: SavedCartApiService,
  ) {}
}
